import { ReactElement, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Chip,
  Heading,
  Link,
  Paragraph,
  Skeleton,
} from '@digdir/designsystemet-react';
import {
  useMarkNotificationsSeenMutation,
  useNotificationCenterQuery,
} from '../../hooks/queries/notificationCenter/useNotificationCenterQuery';
import { useMarkNotificationsReadMutation } from '../../hooks/queries/safetyRep/useSafetyRepWork';
import {
  NewsList,
  TaskCards,
} from '../../components/NotificationCenter/NotificationItems';
import {
  badgeCount,
  centerItems,
  ItemRole,
  onlyRole,
  rolesIn,
  seenKeys,
  settingsLinks,
} from './notificationCenterView';
import layout from '../HRDashboard/hmsPage.module.css';
import classes from '../SafetyRounds/Deviations/Deviations.module.css';

const K = 'notificationCenter';

/** Where the bell's «Velg hvilke e-poster du får» leads */
export const SETTINGS_ID = 'innstillinger';

/** «Varsler»: everything that waits for the user across their roles, in one place */
export const NotificationCenterPage = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useNotificationCenterQuery(i18n.language);
  const markRead = useMarkNotificationsReadMutation();
  const markSeen = useMarkNotificationsSeenMutation();
  const [role, setRole] = useState<ItemRole | 'all'>('all');
  const { hash } = useLocation();
  const settingsHeading = useRef<HTMLHeadingElement>(null);
  const seenOnOpen = useRef(false);

  // Opening the page shows all of it, as the bell does, so what is in it is seen. What comes later counts on the bell.
  useEffect(() => {
    if (!data || seenOnOpen.current) return;
    seenOnOpen.current = true;
    const all = centerItems(data, t, i18n.language);
    if (badgeCount(all, data.seen) > 0) markSeen.mutate(seenKeys(all, data));
  }, [data, t, i18n.language, markSeen]);

  // The link to the settings goes to them once the page has its content, and moves the focus there
  useEffect(() => {
    if (hash !== `#${SETTINGS_ID}` || !data) return;
    const heading = settingsHeading.current;
    heading?.scrollIntoView?.({ block: 'start' });
    heading?.focus({ preventScroll: true });
  }, [hash, data]);

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t(`${K}.loading`)}</span>
          <Skeleton variant="rectangle" width="100%" height={240} />
        </div>
      );
    if (error || !data)
      return (
        <Alert data-color="danger">
          <Paragraph>{t(`${K}.failed`)}</Paragraph>
        </Alert>
      );
    const all = centerItems(data, t, i18n.language);
    const roles = rolesIn(all);
    const items = onlyRole(all, role);
    const settings = settingsLinks(data);
    return (
      <>
        {data.unavailable.length > 0 && (
          <Alert data-color="warning" data-size="sm">
            <Paragraph data-size="sm">
              {t(`${K}.unavailable`, {
                modules: data.unavailable
                  .map((part) => t(`${K}.sources.${part}`))
                  .join(', '),
              })}
            </Paragraph>
          </Alert>
        )}
        {roles.length > 1 && (
          <div
            role="group"
            aria-label={t(`${K}.filter`)}
            className={classes.tags}
          >
            {(['all', ...roles] as const).map((option) => (
              <Chip.Radio
                key={option}
                name="notification-role"
                checked={role === option}
                onChange={() => setRole(option)}
              >
                {option === 'all' ? t(`${K}.all`) : t(`${K}.roles.${option}`)}
              </Chip.Radio>
            ))}
          </div>
        )}
        <section aria-labelledby="varsler-waiting" className={classes.section}>
          <Heading level={2} data-size="sm" id="varsler-waiting">
            {t(`${K}.waitingCount`, { count: items.waiting.length })}
          </Heading>
          {items.waiting.length === 0 ? (
            <Paragraph data-size="sm" className={layout.subtle}>
              {t(`${K}.waitingEmpty`)}
            </Paragraph>
          ) : (
            <TaskCards items={items.waiting} />
          )}
        </section>
        <section aria-labelledby="varsler-info" className={classes.section}>
          <div className={classes.header}>
            <Heading level={2} data-size="sm" id="varsler-info">
              {t(`${K}.info`)}
            </Heading>
          </div>
          {items.info.length > 0 ? (
            <>
              <NewsList items={items.info} />
              <div>
                <Button
                  variant="secondary"
                  data-size="sm"
                  loading={markRead.isPending}
                  onClick={() => markRead.mutate(null)}
                >
                  {t(`${K}.markAllRead`)}
                </Button>
              </div>
            </>
          ) : (
            <Paragraph data-size="sm" className={layout.subtle}>
              {t(`${K}.infoEmpty`)}
            </Paragraph>
          )}
        </section>
        {items.read.length > 0 && (
          <section aria-labelledby="varsler-read" className={classes.section}>
            <Heading level={2} data-size="sm" id="varsler-read">
              {t(`${K}.read`)}
            </Heading>
            <NewsList items={items.read} />
          </section>
        )}
        <section
          aria-labelledby="innstillinger-title"
          id={SETTINGS_ID}
          className={classes.section}
        >
          <Heading level={2} data-size="sm" id="innstillinger-title" ref={settingsHeading} tabIndex={-1}>
            {t(`${K}.settingsTitle`)}
          </Heading>
          {settings.length === 0 ? (
            <Paragraph data-size="sm" className={layout.subtle}>
              {t(`${K}.settingsNone`)}
            </Paragraph>
          ) : (
            <ul className={classes.list}>
              {settings.map((setting) => (
                <li key={setting.key}>
                  <Link asChild>
                    <RouterLink to={setting.to}>
                      {t(`${K}.settingsLinks.${setting.key}`)}
                    </RouterLink>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </>
    );
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <Heading level={1} data-size="xl">
            {t(`${K}.title`)}
          </Heading>
          <Paragraph className={layout.intro}>{t(`${K}.intro`)}</Paragraph>
        </div>
      </section>
      <div className={classes.content}>{content()}</div>
    </div>
  );
};
